import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Injector, inject, NgZone, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environment/environment';
import { TranslocoService } from '@jsverse/transloco';
import type {
	LoginResponseSchemaDTO,
	LogoutResponseSchemaDTO,
	ResetPasswordResponseSchemaDTO,
	UpdatePasswordWithTokenResponseSchemaDTO,
} from '@libs/dtos/auth/auth.dto';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { ProviderBalanceService } from '@services/provider/provider-balance.service';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, finalize, map, retry, shareReplay, tap } from 'rxjs/operators';
import { apiErrorCode, translateApiError } from '../../utils/api-error';
import { CurrentUserService } from './current-user.service';

/** Session returned by login, refresh, register, dev-login and MFA login */
export type AuthSessionResponse = Omit<LoginResponseSchemaDTO, 'user'> & {
	mfa_required?: undefined;
	user: LoginResponseSchemaDTO['user'] & { user_metadata?: { email_verified?: boolean } };
	userData: {
		id: number;
		name: string;
		lastName: string;
		lastName2: string | null;
		email: string;
		profilePhotoUrl: string | null;
	};
	role: { id: number; name: string; description?: string | null };
	permissions: string[];
	/** Only after an MFA login that trusted the device */
	device_token?: string;
};

/** Login of a user with MFA: a temporary token and the factor to verify, no session yet */
export interface MfaChallengeResponse {
	mfa_required: true;
	access_token: string;
	factor_id: string;
	user_id: string;
	email: string;
}

export type LoginResponse = AuthSessionResponse | MfaChallengeResponse;

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private readonly TOKEN_KEY = 'JWT_TOKEN';
	private readonly REFRESH_TOKEN_KEY = 'refresh_token';
	private readonly DEVICE_TOKEN_KEY = 'device_token';
	private readonly loginUrl = `${environment.pathBackend}/auth/login`;
	private readonly refreshUrl = `${environment.pathBackend}/auth/refresh`;
	private refreshTokenTimeout: ReturnType<typeof setTimeout> | null = null;

	private readonly injector = inject(Injector);

	/** Resolved lazily: injecting it eagerly would pull HttpClient (and its interceptors) into AuthService's construction. */
	private get transloco(): TranslocoService {
		return this.injector.get(TranslocoService);
	}

	private translate(key: string, params?: Record<string, unknown>): string {
		return this.transloco.translate(key, params);
	}

	constructor(
		private http: HttpClient,
		private router: Router,
		private messageAlertService: MessageAlertService,
		private currentUserService: CurrentUserService,
		private ngZone: NgZone,
		@Inject(PLATFORM_ID) private platformId: object,
	) {}

	/**
	 * Gets the current access token
	 * @returns The JWT token or null if it doesn't exist
	 */
	getToken(): string | null {
		return this.getLocalStorage(this.TOKEN_KEY);
	}

	private getLocalStorage(key: string): string | null {
		if (isPlatformBrowser(this.platformId)) {
			return localStorage.getItem(key);
		}
		return null;
	}

	private setLocalStorage(key: string, value: string): void {
		if (isPlatformBrowser(this.platformId)) {
			localStorage.setItem(key, value);
		}
	}

	private removeLocalStorage(key: string): void {
		if (isPlatformBrowser(this.platformId)) {
			localStorage.removeItem(key);
		}
	}

	/**
	 * Logs in by sending credentials to the backend.
	 * @param email User's email
	 * @param password User's password
	 * @returns Observable with backend response
	 */
	login(email: string, password: string): Observable<LoginResponse> {
		const deviceToken = this.getLocalStorage(this.DEVICE_TOKEN_KEY);

		const loginData: { email: string; password: string; deviceToken?: string } = {
			email: email,
			password: password,
		};

		if (deviceToken) {
			loginData.deviceToken = deviceToken;
		}

		return this.http.post<LoginResponse>(this.loginUrl, loginData).pipe(
			tap((response) => this.handleLoginResponse(response)),
			catchError((error: HttpErrorResponse) => {
				// errors.<code> (e.g. AUTH_INVALID_CREDENTIALS); without a known code, by status
				const fallbackKey =
					error.status === 401 ? 'authMessages.incorrectPassword' : 'authMessages.internalServerError';
				const errorMessage = translateApiError(error, this.transloco, fallbackKey);
				this.messageAlertService.showMessage('error', this.translate('common.labels.error'), errorMessage);
				return throwError(() => Object.assign(new Error(errorMessage), { code: apiErrorCode(error) }));
			}),
		);
	}

	getDevLoginUsers(): Observable<Array<{ id: string; email: string; name: string; role: string | null }>> {
		return this.http.get<Array<{ id: string; email: string; name: string; role: string | null }>>(
			`${environment.pathBackend}/auth/dev-login/users`,
		);
	}

	devLogin(userId: string): Observable<LoginResponse> {
		return this.http
			.post<LoginResponse>(`${environment.pathBackend}/auth/dev-login`, { userId })
			.pipe(tap((response) => this.handleLoginResponse(response)));
	}

	private handleLoginResponse(response: LoginResponse): void {
		// If MFA is required, return early without processing the login
		if (response?.mfa_required) {
			return; // Exit early, let the component handle MFA
		}

		if (!response?.access_token) {
			throw new Error('No access token received');
		}

		this.persistSession(response);
		this.navigateAfterLogin();
	}

	/**
	 * Starts a session from an auth answer (login, register, dev-login, MFA login): tokens, refresh timer,
	 * the trusted-device token when the MFA step trusted the device, and the user with role/permissions.
	 */
	persistSession(response: AuthSessionResponse): void {
		this.persistTokens(response.access_token, response.refresh_token);
		if (response.device_token) {
			this.setLocalStorage(this.DEVICE_TOKEN_KEY, response.device_token);
		}
		this.saveUserFromAuthResponse(response);
	}

	/** Stores a token pair and schedules its refresh (also used when MFA upgrades the session) */
	persistTokens(accessToken: string, refreshToken: string): void {
		this.setLocalStorage(this.TOKEN_KEY, accessToken);
		this.setLocalStorage(this.REFRESH_TOKEN_KEY, refreshToken);
		this.startRefreshTokenTimer({ access_token: accessToken });
	}

	/**
	 * After a login: the returnUrl of the login page (set by the guards) when it is an internal path,
	 * otherwise boost. Read before navigating, while the router is still on /login?returnUrl=...
	 */
	navigateAfterLogin(): void {
		const returnUrl = this.router.parseUrl(this.router.url).queryParams['returnUrl'];
		const target = AuthService.isSafeReturnUrl(returnUrl) ? returnUrl : '/boost';
		// Inside the Angular zone (the login answer may arrive outside it)
		this.ngZone.run(() => {
			setTimeout(() => {
				this.router.navigateByUrl(target);
			}, 150);
		});
	}

	/** Only an internal path: starts with one '/' (not '//' or '/\\', other hosts) and is not the login again */
	static isSafeReturnUrl(url: unknown): url is string {
		if (typeof url !== 'string' || !url.startsWith('/')) return false;
		if (url.startsWith('//') || url.startsWith('/\\')) return false;
		return !/^\/login(?:[/?#]|$)/.test(url);
	}

	/**
	 * Registers a new user account
	 * @param userData User registration data
	 * @returns Observable with registration response
	 */
	register(userData: { email: string; password: string; referralCode?: string }): Observable<AuthSessionResponse> {
		return this.http.post<AuthSessionResponse>(`${environment.pathBackend}/auth/register`, userData).pipe(
			tap((response) => {
				if (!response?.access_token) {
					throw new Error('No access token received');
				}

				// Auto-login after registration
				this.persistSession(response);

				this.messageAlertService.showMessage(
					'success',
					this.translate('authMessages.accountCreatedTitle'),
					this.translate('authMessages.accountCreatedMessage'),
				);

				// Navigate to boost page
				this.ngZone.run(() => {
					setTimeout(() => {
						this.router.navigate(['/boost']);
					}, 150);
				});
			}),
			catchError((error: HttpErrorResponse) => {
				// errors.<code> (e.g. EMAIL_ALREADY_REGISTERED); generic key otherwise
				const code = apiErrorCode(error);
				const errorMessage = translateApiError(error, this.transloco, 'authMessages.registerError');
				this.messageAlertService.showMessage('error', this.translate('common.labels.error'), errorMessage);

				// 503: the account exists but the automatic login failed, so the next step is logging in
				if (code === 'REGISTERED_LOGIN_FAILED') {
					this.ngZone.run(() => this.router.navigate(['/login']));
				}
				// The code travels with the error so the callers can react (the auth modal closes)
				return throwError(() => Object.assign(new Error(errorMessage), { code }));
			}),
		);
	}

	/**
	 * Logs out by sending refresh token to backend and removing tokens from local storage.
	 * @returns Observable with backend response
	 */
	logout(): Observable<LogoutResponseSchemaDTO | null> {
		// Get both tokens before clearing them
		const refreshToken = this.getLocalStorage(this.REFRESH_TOKEN_KEY);
		const accessToken = this.getLocalStorage(this.TOKEN_KEY);

		// Stop timer and clear tokens
		this.stopRefreshTokenTimer();
		this.removeLocalStorage(this.TOKEN_KEY);
		this.removeLocalStorage(this.REFRESH_TOKEN_KEY);

		// Clear user data (keep brand colors — they're not sensitive)
		this.currentUserService.clearCurrentUser();
		// Provider balance is admin data: stop its poll and forget it (resolved lazily, no DI cycle)
		this.injector.get(ProviderBalanceService).stop();

		// If both tokens exist, notify backend (both are required by external auth service)
		if (refreshToken && accessToken) {
			return this.http
				.post<LogoutResponseSchemaDTO>(`${environment.pathBackend}/auth/logout`, {
					refresh_token: refreshToken,
					access_token: accessToken,
				})
				.pipe(
					tap(() => {
						this.router.navigate(['/login']);
					}),
					catchError(() => {
						// Even if it fails, navigate to login
						this.router.navigate(['/login']);
						return of(null);
					}),
				);
		}

		// If no tokens available, just navigate to login
		this.router.navigate(['/login']);
		return of(null);
	}

	/**
	 * Checks if the user is authenticated.
	 * Returns true if there's a valid token OR a refresh token (session can be restored).
	 */
	isAuthenticated(): boolean {
		if (!isPlatformBrowser(this.platformId)) {
			return false;
		}

		const token = this.getLocalStorage(this.TOKEN_KEY);
		if (!token) {
			return false;
		}

		try {
			const payload = JSON.parse(atob(token.split('.')[1]));
			const isExpired = payload.exp * 1000 < Date.now();

			if (isExpired) {
				// Token expired but refresh token exists = still authenticated (will refresh)
				const refreshToken = this.getLocalStorage(this.REFRESH_TOKEN_KEY);
				return !!refreshToken;
			}

			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Checks if the access token is expired and needs refresh
	 */
	isTokenExpired(): boolean {
		const token = this.getLocalStorage(this.TOKEN_KEY);
		if (!token) return true;

		try {
			const payload = JSON.parse(atob(token.split('.')[1]));
			return payload.exp * 1000 < Date.now();
		} catch {
			return true;
		}
	}

	/**
	 * Initializes auth on app startup. If token is expired but refresh token exists,
	 * refreshes the token automatically so the user stays logged in.
	 */
	initAuth(): Observable<boolean> {
		if (!isPlatformBrowser(this.platformId)) {
			return of(false);
		}

		const token = this.getLocalStorage(this.TOKEN_KEY);
		const refreshTokenValue = this.getLocalStorage(this.REFRESH_TOKEN_KEY);

		if (!token || !refreshTokenValue) {
			return of(false);
		}

		if (this.isTokenExpired()) {
			// Token expired, try to refresh
			return this.refreshToken({ logoutOnFailure: false }).pipe(
				map(() => true),
				catchError(() => {
					// Refresh failed, clear everything
					this.removeLocalStorage(this.TOKEN_KEY);
					this.removeLocalStorage(this.REFRESH_TOKEN_KEY);
					this.currentUserService.clearCurrentUser();
					return of(false);
				}),
			);
		}

		// Token still valid, just start the refresh timer
		this.startRefreshTokenTimer({ access_token: token });
		return of(true);
	}

	/** The refresh in progress, shared by every caller (parallel 401s wait for the same one) */
	private refreshInFlight$: Observable<AuthSessionResponse> | null = null;
	private readonly maxRefreshRetries = 3;

	/**
	 * Refreshes the access token using the refresh token. Concurrent calls share one request.
	 * Only a network error (status 0) or a 5xx is retried (2s, 4s, 8s): a 4xx means the refresh token
	 * is invalid or revoked, and retrying it only kept the app blank for ~14s on reload.
	 * @param options.logoutOnFailure When the refresh finally fails, log out (clear the session and go
	 *   to /login) once. initAuth passes false: at startup it only clears the session and the guards
	 *   send protected routes to /login (with returnUrl), public pages stay.
	 */
	refreshToken(options: { logoutOnFailure?: boolean } = {}): Observable<AuthSessionResponse> {
		if (this.refreshInFlight$) {
			return this.refreshInFlight$;
		}

		const refreshToken = this.getLocalStorage(this.REFRESH_TOKEN_KEY);
		if (!refreshToken) {
			return throwError(() => new Error('No refresh token available'));
		}

		const logoutOnFailure = options.logoutOnFailure ?? true;
		this.refreshInFlight$ = this.http
			.post<AuthSessionResponse>(this.refreshUrl, {
				refresh_token: refreshToken,
			})
			.pipe(
				retry({
					count: this.maxRefreshRetries,
					delay: (error, retryCount) =>
						this.isRetryableRefreshError(error) ? timer(2 ** retryCount * 1000) : throwError(() => error),
				}),
				tap((response) => {
					if (!response?.access_token || !response?.refresh_token) {
						throw new Error('Invalid refresh response');
					}

					this.persistTokens(response.access_token, response.refresh_token);
					// The refresh answers with the user too: keeps role and permissions current (e.g. a
					// permission granted after the login) without logging in again
					if (response.userData && response.role) {
						this.saveUserFromAuthResponse(response);
					}
				}),
				catchError((error) => {
					// Once per failed refresh, not once per waiting request
					if (logoutOnFailure) {
						this.logout().subscribe();
					}
					return throwError(() => error);
				}),
				finalize(() => {
					this.refreshInFlight$ = null;
				}),
				shareReplay({ bufferSize: 1, refCount: false }),
			);

		return this.refreshInFlight$;
	}

	private isRetryableRefreshError(error: unknown): boolean {
		return error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500);
	}

	/**
	 * Starts the timer to refresh the token
	 * @param tokens Access and refresh tokens
	 */
	private startRefreshTokenTimer(tokens: { access_token: string }) {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		// Decode token to get expiration time
		const jwtToken = JSON.parse(atob(tokens.access_token.split('.')[1]));
		const expiresAt = new Date(jwtToken.exp * 1000);
		const now = new Date();
		const timeUntilExpiry = expiresAt.getTime() - now.getTime();

		// If token expires in less than 2 minutes, refresh immediately
		if (timeUntilExpiry < 120000) {
			// A failed refresh already logs out
			this.refreshToken().subscribe({ error: () => {} });
			return;
		}

		// Refresh when 1/3 of token lifetime remains
		const refreshDelay = Math.floor(timeUntilExpiry * 0.66);

		// Clear any existing timeout
		if (this.refreshTokenTimeout) {
			clearTimeout(this.refreshTokenTimeout);
		}

		// Set new timeout
		this.refreshTokenTimeout = setTimeout(() => {
			// A failed refresh (after its retries on network/5xx errors) already logs out
			this.refreshToken().subscribe({ error: () => {} });
		}, refreshDelay);
	}

	/**
	 * Stops the token refresh timer
	 */
	private stopRefreshTokenTimer() {
		if (this.refreshTokenTimeout) {
			clearTimeout(this.refreshTokenTimeout);
		}
	}

	/**
	 * Sends a password reset email to the user
	 * @param email User's email address
	 * @returns Observable with the reset response
	 */
	resetPassword(email: string): Observable<ResetPasswordResponseSchemaDTO> {
		return this.http
			.post<ResetPasswordResponseSchemaDTO>(`${environment.pathBackend}/auth/reset-password`, { email })
			.pipe(
				catchError((error: HttpErrorResponse) => {
					const errorMessage = translateApiError(error, this.transloco, 'authMessages.passwordResetError');
					this.messageAlertService.showMessage('error', this.translate('common.labels.error'), errorMessage);
					return throwError(() => new Error(errorMessage));
				}),
			);
	}

	/**
	 * Updates user password using the access token from reset email
	 * @param accessToken Token from reset email
	 * @param newPassword New password
	 * @returns Observable with the update response
	 */
	updatePasswordWithToken(
		accessToken: string,
		newPassword: string,
	): Observable<UpdatePasswordWithTokenResponseSchemaDTO> {
		return this.http
			.post<UpdatePasswordWithTokenResponseSchemaDTO>(`${environment.pathBackend}/auth/update-password`, {
				accessToken,
				newPassword,
			})
			.pipe(
				catchError((error: HttpErrorResponse) => {
					const errorMessage = translateApiError(error, this.transloco, 'authMessages.passwordUpdateError');
					this.messageAlertService.showMessage('error', this.translate('common.labels.error'), errorMessage);
					return throwError(() => new Error(errorMessage));
				}),
			);
	}

	/**
	 * Stores the logged-in user from an auth response (login, register, MFA).
	 * The backend sends `userData`, `role` and `permissions` at the top level.
	 */
	private saveUserFromAuthResponse(response: AuthSessionResponse): void {
		const { userData, user, role } = response;

		if (!userData) {
			throw new Error('No user data received');
		}
		if (!role) {
			throw new Error('No role data received');
		}

		this.currentUserService.setCurrentUser({
			id: user.id, // Supabase user ID
			userId: userData.id, // Database user ID
			email: userData.email,
			displayName:
				`${userData.name || ''} ${userData.lastName || ''}`.trim() || this.translate('common.labels.user'),
			role: { id: role.id, name: role.name, description: role.description },
			permissions: response.permissions || [],
			enabled: true,
			emailVerified: user.user_metadata?.email_verified || false,
			profilePhotoUrl: userData.profilePhotoUrl || null,
		});
	}
}
